import type { InstantSearch } from "instantsearch.js";
import {
    rangeFiltersState,
    subscribeRangeFiltersState,
    updateRangeFilter,
    type RangeFiltersState,
    type RangeValue,
} from "@/services/algoliaRouting.blade";

type RangeAttribute = keyof Omit<RangeFiltersState, "specification">;

function resolveFacetContainers(selector: string | string[]): HTMLElement[] {
    const selectors = Array.isArray(selector) ? selector : [selector];
    return selectors.flatMap((sel) => {
        const el = document.querySelector<HTMLElement>(sel);
        return el ? [el] : [];
    });
}

function readRangeConfig(container: HTMLElement) {
    return {
        minLabel: container.dataset.minLabel ?? "Minimum",
        maxLabel: container.dataset.maxLabel ?? "Maximum",
        unit: container.dataset.unit ?? "",
        // Complément d'unité affiché sur mobile seulement (« €/mois »), hors messages d'erreur.
        unitSuffix: container.dataset.unitSuffix ?? "",
        showMin: container.dataset.showMin !== "false",
        showMax: container.dataset.showMax !== "false",
    };
}

function buildRangeHtml(config: ReturnType<typeof readRangeConfig>): string {
    const { minLabel, maxLabel, unit, unitSuffix, showMin, showMax } = config;
    const unitHtml = unitSuffix
        ? `${unit}<span class="input-inner-label__unit-suffix">${unitSuffix}</span>`
        : unit;
    // placeholder « » : invisible, il sert au CSS mobile à masquer le libellé dès qu'une valeur est saisie.
    const inputAttrs = (label: string) =>
        `type="text" placeholder=" "${label ? ` aria-label="${label}"` : ""}`;
    const cols = showMin && showMax ? "1fr 0.1fr 1fr" : "1fr";
    let html = `<div class="non-refining-range__wrapper" style="grid-template-columns:${cols}">`;

    if (showMin) {
        html += `
                        <div class="input-inner-label" data-attr="listing-panel-range" data-ph-capture-attribute-range_bound="min">
                            <span class="input-inner-label__unit">${unitHtml}</span>
                            <label class="input-inner-label__label">${minLabel}</label>
                            <input class="input-inner-label__input" ${inputAttrs(minLabel)} data-range="min" />
                        </div>`;
    }

    if (showMin && showMax) {
        html += `<div class="non-refining-range__separation"></div>`;
    }

    if (showMax) {
        html += `
                        <div class="input-inner-label" data-attr="listing-panel-range" data-ph-capture-attribute-range_bound="max">
                            <span class="input-inner-label__unit">${unitHtml}</span>
                            <label class="input-inner-label__label">${maxLabel}</label>
                            <input class="input-inner-label__input" ${inputAttrs(maxLabel)} data-range="max" />
                        </div>`;
    }

    html += `</div>`;
    // Toujours présent : un champ seul (maximum du panneau Filtrer) peut aussi contredire l'autre
    // borne, choisie ailleurs (pastille de la barre).
    html += `<p class="non-refining-range__error" data-range-error role="alert" hidden>${RANGE_ORDER_ERROR}</p>`;
    return html;
}

export const RANGE_ORDER_ERROR = "Le minimum doit être inférieur au maximum";

/**
 * Nombre saisi : espaces de milliers (normale, insécable, fine insécable) ignorés, virgule
 * décimale acceptée. `undefined` pour un champ vide, `null` pour une saisie illisible.
 */
export function parseRangeNumber(raw: string): number | undefined | null {
    const normalized = raw.replace(/[\s\u00a0\u202f]/g, "").replace(",", ".");
    if (normalized === "") return undefined;
    const value = Number(normalized);
    return Number.isFinite(value) ? value : null;
}

/** Vrai quand les deux bornes sont saisies et que le minimum dépasse le maximum. */
export function isRangeOrderInvalid(minValue: string, maxValue: string): boolean {
    const min = parseRangeNumber(minValue);
    const max = parseRangeNumber(maxValue);
    return typeof min === "number" && typeof max === "number" && min > max;
}

function formatBound(value: number): string {
    const [int, dec] = String(value).split(".");
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, "\u202f");
    return dec ? `${grouped},${dec}` : grouped;
}

/**
 * Message quand le minimum dépasse le maximum. Si une borne n'a pas de champ ici (elle vient de
 * l'autre emplacement), le message la cite : « Le maximum doit être supérieur au minimum de 500 € ».
 */
export function rangeOrderMessage(
    range: RangeValue,
    showMin: boolean,
    showMax: boolean,
    unit: string,
): string | null {
    const { min, max } = range;
    if (min === undefined || max === undefined || min <= max) return null;
    const suffix = unit ? `\u00a0${unit}` : "";
    if (!showMin) return `Le maximum doit être supérieur au minimum de ${formatBound(min)}${suffix}`;
    if (!showMax) return `Le minimum doit être inférieur au maximum de ${formatBound(max)}${suffix}`;
    return RANGE_ORDER_ERROR;
}

/**
 * Plage saisie dans `container`, et message d'ordre éventuel. Une borne que ce conteneur
 * n'affiche pas garde sa valeur courante (`current`) : le champ « maximum » seul du panneau
 * Filtrer ne retire pas le minimum choisi dans la pastille de la barre.
 */
export function evaluateRangeContainer(
    container: HTMLElement,
    current: RangeValue = {},
): { value: RangeValue | null; message: string | null } {
    const { showMin, showMax, unit } = readRangeConfig(container);
    const minInput = container.querySelector<HTMLInputElement>('[data-range="min"]');
    const maxInput = container.querySelector<HTMLInputElement>('[data-range="max"]');

    const min = showMin ? parseRangeNumber(minInput?.value ?? "") : current.min;
    const max = showMax ? parseRangeNumber(maxInput?.value ?? "") : current.max;
    if (min === null || max === null) return { value: null, message: null };

    const range = { min, max };
    const message = rangeOrderMessage(range, showMin, showMax, unit);
    return { value: message ? null : range, message };
}

/** Message sous les champs et champs signalés tant que le minimum dépasse le maximum. */
function syncRangeOrderError(container: HTMLElement, current: RangeValue): void {
    const error = container.querySelector<HTMLElement>("[data-range-error]");
    if (!error) return;

    const { message } = evaluateRangeContainer(container, current);
    error.hidden = message === null;
    if (message) error.textContent = message;
    container.querySelectorAll<HTMLInputElement>("[data-range]").forEach((input) => {
        if (message) {
            input.setAttribute("aria-invalid", "true");
        } else {
            input.removeAttribute("aria-invalid");
        }
    });
}

function syncRangeInputs(
    containers: HTMLElement[],
    range: RangeValue,
    hasPendingEntry: (container: HTMLElement) => boolean = () => false,
): void {
    for (const container of containers) {
        // Saisie pas encore appliquée : on ne la remplace pas par l'état courant
        if (hasPendingEntry(container)) continue;
        const { showMin, showMax } = readRangeConfig(container);
        const minInput = container.querySelector(
            '[data-range="min"]',
        ) as HTMLInputElement | null;
        const maxInput = container.querySelector(
            '[data-range="max"]',
        ) as HTMLInputElement | null;

        if (showMin && minInput && document.activeElement !== minInput) {
            minInput.value = range.min !== undefined ? String(range.min) : "";
        }
        if (showMax && maxInput && document.activeElement !== maxInput) {
            maxInput.value = range.max !== undefined ? String(range.max) : "";
        }
        syncRangeOrderError(container, range);
    }
}

export const RANGE_INPUT_DELAY_MS = 500;

/**
 * Une saisie par emplacement (pastille, panneau Filtrer), appliquée après un court délai. Taper
 * dans un autre emplacement applique d'abord la saisie en attente : aucune n'est perdue.
 */
export function createRangeInputScheduler<T>(apply: (source: T) => void, delayMs = RANGE_INPUT_DELAY_MS) {
    const pending = new Map<T, ReturnType<typeof setTimeout>>();
    const flush = (source: T) => {
        const timer = pending.get(source);
        if (timer === undefined) return;
        clearTimeout(timer);
        pending.delete(source);
        apply(source);
    };

    return {
        schedule(source: T): void {
            // Saisie en cours marquée d'abord : appliquer l'autre emplacement ne doit pas l'écraser
            const timer = pending.get(source);
            if (timer !== undefined) clearTimeout(timer);
            pending.set(
                source,
                setTimeout(() => {
                    pending.delete(source);
                    apply(source);
                }, delayMs),
            );
            for (const other of [...pending.keys()]) {
                if (other !== source) flush(other);
            }
        },
        flush,
        isPending: (source: T): boolean => pending.has(source),
    };
}

function bindRangeInputs(
    containers: HTMLElement[],
    attribute: RangeAttribute,
): (container: HTMLElement) => boolean {
    const scheduler = createRangeInputScheduler((sourceContainer: HTMLElement) => {
        const current = rangeFiltersState.value[attribute] ?? {};
        syncRangeOrderError(sourceContainer, current);
        const { value } = evaluateRangeContainer(sourceContainer, current);
        if (value === null) return;
        updateRangeFilter(attribute, value);
    });

    for (const container of containers) {
        container.querySelectorAll("input").forEach((input) => {
            input.addEventListener("input", () => {
                // Une saisie qui rétablit l'ordre retire le message tout de suite ; un nouveau
                // message n'apparaît qu'une fois la saisie finie (pas à chaque chiffre).
                const error = container.querySelector<HTMLElement>("[data-range-error]");
                if (error && !error.hidden) {
                    syncRangeOrderError(container, rangeFiltersState.value[attribute] ?? {});
                }
                scheduler.schedule(container);
            });

            input.addEventListener("keydown", (e) => {
                const controlKeys = [
                    "Backspace",
                    "Tab",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                    "ArrowUp",
                    "ArrowDown",
                    "Delete",
                ];
                // Raccourcis (coller, tout sélectionner…), espace de milliers et virgule décimale permis
                if (e.ctrlKey || e.metaKey || e.key === ",") return;
                if (!controlKeys.includes(e.key) && isNaN(Number(e.key))) {
                    e.preventDefault();
                }
            });
        });
    }

    return scheduler.isPending;
}

/**
 * Non-refining range inputs (Vue: RangeFilterWidget + NonRefiningRange).
 * Filters via configure.filters + rangeFiltersState, not connectRange.
 */
export function createCustomRangeWidget(
    containerSelector: string | string[],
    attribute: RangeAttribute,
    search: InstantSearch,
) {
    const containers = resolveFacetContainers(containerSelector);
    if (!containers.length) return null;

    // Chaque conteneur garde ses champs : le panneau Filtrer peut n'afficher que le maximum
    // (mensualité) quand la pastille de la barre propose minimum et maximum.
    for (const container of containers) {
        container.innerHTML = buildRangeHtml(readRangeConfig(container));
    }

    const hasPendingEntry = bindRangeInputs(containers, attribute);

    const syncUi = () => {
        const range = rangeFiltersState.value[attribute] ?? {};
        syncRangeInputs(containers, range, hasPendingEntry);
    };

    syncUi();

    const stopWatch = subscribeRangeFiltersState(syncUi);

    const onRender = () => syncUi();
    search.on("render", onRender);

    return {
        $$type: "custom.nonRefiningRange",
        $$widgetType: "custom.nonRefiningRange",
        init() {},
        render() {
            syncUi();
        },
        getWidgetUiState(uiState: Record<string, unknown>) {
            // Non-refining ranges flow through configure.filters / rangeFiltersState,
            // not a uiState slice. Pass the accumulated uiState through untouched —
            // returning a fresh object here wipes earlier widgets' contributions
            // (query, refinementList…) during InstantSearch's getUiState reduce.
            return uiState;
        },
        dispose() {
            search.removeListener("render", onRender);
            stopWatch();
        },
    };
}
